import { Api } from 'lib';

/**
 * Uploads a file straight to S3 using a presigned POST issued by the server.
 *
 * REACT_APP_AWS_S3_ENDPOINT exists only for local S3 stand-ins (localstack /
 * MinIO), where the server signs an internal hostname the browser cannot reach
 * and no CORS rules are configured. Against real S3 it must stay unset: it
 * forces `no-cors`, which makes the response opaque, so every rejection (bad
 * policy, expired signature, size limit) is silently swallowed and the caller
 * stores a URL for an object that was never written.
 */
async function uploadWithSignedUrl(key, contentType, file) {
  /*
    Get upload url with rules from server
  */
  const signedUrl = await Api.post('/services/getUploadUrl', { key, contentType });

  if (!signedUrl?.fields) {
    throw new Error('Upload failed: server did not return a presigned post');
  }

  const localEndpoint = process.env.REACT_APP_AWS_S3_ENDPOINT;
  const target = localEndpoint || signedUrl.url;

  const formData = new FormData();

  /*
    Append rules from response to formData
    IMPORTANT: Content-Type is required and must match the signed policy
  */
  formData.append('Content-Type', contentType);
  Object.entries(signedUrl.fields).forEach(([k, v]) => {
    formData.append(k, v);
  });
  /*
    This has to be the last — S3 ignores every field appended after `file`
  */
  formData.append('file', file);

  const init = { method: 'POST', body: formData };

  if (localEndpoint) {
    init.mode = 'no-cors';
  }

  const res = await fetch(target, init);

  /*
    An opaque response (local endpoint, no-cors) carries no status and cannot
    be inspected. Anything readable must be checked, otherwise a rejected
    upload looks identical to a successful one.
  */
  if (res.type !== 'opaque' && !res.ok) {
    const detail = await res.text().catch(() => '');

    throw new Error(`S3 rejected the upload (${res.status}): ${detail.slice(0, 300)}`);
  }

  /*
    NOTE: a successful presigned POST answers 204 with no usable body, so the
    public URL is composed from the key we just uploaded under.
  */
  const baseUrl = process.env.REACT_APP_AWS_S3_BASE_URL;

  if (!baseUrl) {
    throw new Error('Upload failed: REACT_APP_AWS_S3_BASE_URL is not configured');
  }

  return `${baseUrl}/${key}`;
}

export default uploadWithSignedUrl;
